import { getApp, getApps, initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { getAnalytics, logEvent, isSupported as isAnalyticsSupported } from "firebase/analytics";

// Firebase configuration using environment variables
const firebaseConfig = {
  apiKey: "AIzaSyCMoCpBkpvtc06LP8LvE-7-r2kM5hHLn7I",
  authDomain: "cubenation-app.firebaseapp.com",
  databaseURL: "https://cubenation-app.firebaseio.com",
  projectId: "cubenation-app",
  storageBucket: "cubenation-app.firebasestorage.app",
  messagingSenderId: "415960246912",
  appId: "1:415960246912:web:617bd54c3ed15ccb41d465",
  measurementId: "G-7SLYDE70CB"
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

const messaging = async () => {
  const supported = await isSupported();
  return supported ? getMessaging(app) : null;
};

const analytics = async () => {
  if (typeof window !== 'undefined') {
    try {
      const supported = await isAnalyticsSupported();
      if (supported) {
        return getAnalytics(app);
      } else {
        console.warn('⚠️ Firebase Analytics: Not supported in this environment (cookies may not be available)');
        return null;
      }
    } catch (error) {
      console.warn('⚠️ Firebase Analytics: Failed to initialize -', error.message);
      return null;
    }
  }
  return null;
};

// Analytics helper functions
const trackNotificationReceived = async (payload) => {
  try {
    const analyticsInstance = await analytics();
    if (analyticsInstance) {
      logEvent(analyticsInstance, 'notification_received', {
        notification_title: payload?.notification?.title || 'Unknown',
        notification_body: payload?.notification?.body || 'Unknown',
        has_link: !!(payload?.fcmOptions?.link || payload?.data?.link),
        timestamp: Date.now()
      });
      console.log('📊 Analytics: Notification received event logged');
    }
  } catch (error) {
    console.error('❌ Analytics: Error logging notification received:', error);
  }
};

const trackNotificationClicked = async (payload) => {
  try {
    const analyticsInstance = await analytics();
    if (analyticsInstance) {
      logEvent(analyticsInstance, 'notification_clicked', {
        notification_title: payload?.notification?.title || 'Unknown',
        notification_body: payload?.notification?.body || 'Unknown',
        link_url: payload?.fcmOptions?.link || payload?.data?.link || null,
        timestamp: Date.now()
      });
      console.log('📊 Analytics: Notification clicked event logged');
    }
  } catch (error) {
    console.error('❌ Analytics: Error logging notification clicked:', error);
  }
};

const trackNotificationPermissionGranted = async () => {
  try {
    const analyticsInstance = await analytics();
    if (analyticsInstance) {
      logEvent(analyticsInstance, 'notification_permission_granted', {
        timestamp: Date.now()
      });
      console.log('📊 Analytics: Notification permission granted event logged');
    }
  } catch (error) {
    console.error('❌ Analytics: Error logging permission granted:', error);
  }
};

const trackNotificationPermissionDenied = async () => {
  try {
    const analyticsInstance = await analytics();
    if (analyticsInstance) {
      logEvent(analyticsInstance, 'notification_permission_denied', {
        timestamp: Date.now()
      });
      console.log('📊 Analytics: Notification permission denied event logged');
    }
  } catch (error) {
    console.error('❌ Analytics: Error logging permission denied:', error);
  }
};

const fetchToken = async () => {
  try {
    console.log('🔄 Firebase: Starting token fetch process...');
    console.log('🔍 Firebase: Window and serviceWorker check:', typeof window !== 'undefined' && 'serviceWorker' in navigator);
    
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {      console.log('🔄 Firebase: Registering service worker...');
      
      let registration;
      
      // First, try to get any existing registration
      registration = await navigator.serviceWorker.getRegistration('/');
      
      if (registration) {
        console.log('✅ Firebase: Found existing service worker registration');
        console.log('🔍 Firebase: Existing SW scope:', registration.scope);
        console.log('🔍 Firebase: Existing SW active:', registration.active ? 'Yes' : 'No');
        
        // Check if it's our FCM service worker
        if (registration.active && registration.active.scriptURL.includes('firebase-messaging-sw.js')) {
          console.log('✅ Firebase: Existing registration is our FCM service worker');
        } else {
          console.log('⚠️ Firebase: Existing registration is not our FCM service worker, will try to register new one');
          registration = null;
        }
      }
      
      if (!registration) {
        try {
          // Development SSL certificate workaround
          const isDevelopment = process.env.NODE_ENV === 'development';
          const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.includes('192.168.');
          
          if (isDevelopment && isLocalHost) {
            console.log('🔧 Firebase: Development environment detected, using enhanced registration strategy');
            
            // For SSL certificate issues in development, provide clear guidance
            console.log('💡 Firebase: If you see SSL certificate errors, please:');
            console.log(`   1. Open this URL directly: ${window.location.origin}/firebase-messaging-sw.js`);
            console.log('   2. Accept the SSL certificate warning');
            console.log('   3. Reload this page');
            
            // Try multiple registration strategies for development
            const registrationStrategies = [
              // Strategy 1: Standard registration with updateViaCache
              () => navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                scope: '/',
                updateViaCache: 'none'
              }),
              // Strategy 2: Registration without updateViaCache
              () => navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                scope: '/'
              }),
              // Strategy 3: Registration with minimal options
              () => navigator.serviceWorker.register('/firebase-messaging-sw.js')
            ];
            
            for (let i = 0; i < registrationStrategies.length; i++) {
              try {
                console.log(`🔄 Firebase: Trying registration strategy ${i + 1}...`);
                registration = await registrationStrategies[i]();
                console.log(`✅ Firebase: Service Worker registered successfully with strategy ${i + 1}`);
                break;
              } catch (strategyError) {
                console.warn(`⚠️ Firebase: Registration strategy ${i + 1} failed:`, strategyError.message);
                if (i === registrationStrategies.length - 1) {
                  // Last strategy failed, throw a helpful error
                  const helpfulError = new Error(
                    `Service worker registration failed. This is likely due to SSL certificate issues. ` +
                    `Please visit ${window.location.origin}/firebase-messaging-sw.js directly and accept the certificate.`
                  );
                  helpfulError.originalError = strategyError;
                  throw helpfulError;
                }
              }
            }
          } else {
            // Production registration
            console.log('🔧 Firebase: Production environment, using standard registration');
            registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
              scope: '/',
              updateViaCache: 'none'
            });
            console.log('✅ Firebase: Service Worker registered successfully (production)');
          }
          
          console.log('🔍 Firebase: SW Registration scope:', registration.scope);
          console.log('🔍 Firebase: SW Registration active:', registration.active ? 'Yes' : 'No');
          
        } catch (swError) {
          console.error('❌ Firebase: Service worker registration failed:', swError);
          console.error('❌ Firebase: Error details:', swError.message);
            if (swError.message && swError.message.includes('SSL certificate')) {
            console.error('🚨 Firebase: SSL Certificate Error Detected!');
            console.log('💡 Firebase: SOLUTION: To fix this SSL certificate issue:');
            console.log(`   1. Click this link: ${window.location.origin}/firebase-messaging-sw.js`);
            console.log('   2. You will see a security warning - click "Advanced" then "Proceed to localhost (unsafe)"');
            console.log('   3. After accepting the certificate, reload this page');
            console.log('   4. The service worker should then register successfully');
            
            // Make it easier for the user by providing a clickable solution
            if (typeof window !== 'undefined') {
              console.log('%c🔗 CLICK HERE TO ACCEPT SSL CERTIFICATE', 'color: white; background: red; padding: 10px; font-size: 16px; font-weight: bold; border-radius: 5px;');
              console.log(`%c${window.location.origin}/firebase-messaging-sw.js`, 'color: blue; text-decoration: underline; font-size: 14px; cursor: pointer;');
              
              // Store the URL in a global variable for easy access
              window.fcmServiceWorkerUrl = `${window.location.origin}/firebase-messaging-sw.js`;
              console.log('💾 Firebase: Service worker URL stored in window.fcmServiceWorkerUrl');
              console.log('💡 Firebase: You can also run this command: window.open(window.fcmServiceWorkerUrl)');
            }
          }
          
          // Don't throw immediately, try to continue without service worker for now
          console.warn('⚠️ Firebase: Continuing without service worker registration');
          registration = null;
        }
      }
      
      // Wait for service worker to be ready if we have a registration
      if (registration) {
        try {
          const readyPromise = navigator.serviceWorker.ready;
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Service worker ready timeout')), 15000)
          );
          
          await Promise.race([readyPromise, timeoutPromise]);
          console.log('✅ Firebase: Service worker is ready');
        } catch (readyError) {
          console.warn('⚠️ Firebase: Service worker ready timeout, continuing anyway...', readyError.message);
        }
      }
      
      const fcmMessaging = await messaging();
      console.log('🔍 Firebase: Messaging instance:', fcmMessaging ? 'Available' : 'Not available');
        if (fcmMessaging) {
        if (!registration) {
          console.error('❌ Firebase: Cannot request FCM token without service worker registration');
          console.log('💡 Firebase: Please fix the SSL certificate issue first:');
          console.log(`   1. Visit: ${window.location.origin}/firebase-messaging-sw.js`);
          console.log('   2. Accept the SSL certificate warning');
          console.log('   3. Reload this page');
          return null;
        }
        
        console.log('🔑 Firebase: Requesting FCM token...');
        console.log('🔍 Firebase: VAPID Key available:', process.env.NEXT_PUBLIC_FIREBASE_FCM_VAPID_KEY ? 'Yes' : 'No');
        
        const token = await getToken(fcmMessaging, {
          vapidKey: process.env.NEXT_PUBLIC_FIREBASE_FCM_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        
        console.log('🔍 Firebase: Token result:', token ? 'Success' : 'Failed');
        
        if (token) {
          console.log('✅ Firebase: FCM Token obtained:', token.substring(0, 20) + '...');
          console.log('📋 Firebase: FULL FCM TOKEN (copy this):', token);
          console.log('🔗 Firebase: Token length:', token.length);
          
          // Track token generation in analytics (with error handling)
          try {
            const analyticsInstance = await analytics();
            if (analyticsInstance) {
              // Determine browser type
              let browserType = 'Other';
              if (navigator.userAgent.includes('Chrome')) {
                browserType = 'Chrome';
              } else if (navigator.userAgent.includes('Firefox')) {
                browserType = 'Firefox';
              } else if (navigator.userAgent.includes('Safari')) {
                browserType = 'Safari';
              }
              
              logEvent(analyticsInstance, 'notification_token_generated', {
                token_length: token.length,
                browser: browserType,
                timestamp: Date.now()
              });
              console.log('📊 Analytics: Token generation event logged');
            }
          } catch (analyticsError) {
            console.warn('⚠️ Analytics: Could not log token generation event:', analyticsError.message);
          }
          
          // Also make it easy to copy by storing in a global variable
          if (typeof window !== 'undefined') {
            window.fcmToken = token;
            console.log('💾 Firebase: Token stored in window.fcmToken for easy copying');
          }
        }
        
        return token;
      } else {
        console.warn('⚠️ Firebase: Messaging not supported or not available');
      }
    } else {
      console.warn('⚠️ Firebase: Service workers not supported');
    }
    return null;
  } catch (err) {
    console.error("❌ Firebase: An error occurred while fetching the token:", err);
    console.error("❌ Firebase: Error details:", err.message);
    console.error("❌ Firebase: Error stack:", err.stack);
    return null;
  }
};

// Helper function to open service worker URL for SSL certificate acceptance
const openServiceWorkerForSSL = () => {
  if (typeof window !== 'undefined') {
    const swUrl = `${window.location.origin}/firebase-messaging-sw.js`;
    console.log('🔗 Opening service worker URL for SSL certificate acceptance...');
    window.open(swUrl, '_blank');
  }
};

export { app, messaging, analytics, fetchToken, trackNotificationReceived, trackNotificationClicked, trackNotificationPermissionGranted, trackNotificationPermissionDenied, openServiceWorkerForSSL };
